// A decorative arrival effect: native navigation and page content never wait for it.
const motion = window.matchMedia('(prefers-reduced-motion: reduce)');
const source = document.querySelector('script[data-three-url]');
const navigation = performance.getEntriesByType('navigation')[0];

function claimFirstHomeVisit() {
  if (!document.body.classList.contains('page-type-index')) return false;
  try {
    const key = 'luokavrion:home-entry-seen';
    if (sessionStorage.getItem(key)) return false;
    // Reserve the first visit even if motion is skipped or its asset loads slowly.
    sessionStorage.setItem(key, '1');
    return navigation?.type !== 'reload' && navigation?.type !== 'back_forward';
  } catch {
    // Without session storage, skip the effect instead of repeating it on every visit.
    return false;
  }
}

if (source && claimFirstHomeVisit() && !motion.matches && !navigator.connection?.saveData &&
    document.visibilityState === 'visible') {
  start().catch(() => {});
}

async function start() {
  const requested = performance.now();
  const THREE = await import(source.dataset.threeUrl);
  // Do not interrupt someone who has already started reading a slow-loading page.
  if (performance.now() - requested > 1800 || motion.matches || document.hidden) return;

  let renderer;
  try {
    renderer = new THREE.WebGLRenderer({ alpha: true, antialias: true, powerPreference: 'low-power' });
  } catch { return; }
  const canvas = renderer.domElement;
  canvas.id = 'mecha-page-entry';
  canvas.setAttribute('aria-hidden', 'true');
  canvas.style.cssText = 'position:fixed;inset:0;width:100%;height:100%;pointer-events:none;z-index:1090;';
  renderer.setPixelRatio(Math.min(window.devicePixelRatio, 1.5));
  renderer.setSize(window.innerWidth, window.innerHeight);
  renderer.setClearColor(0x000000, 0);
  const scene = new THREE.Scene();
  const camera = new THREE.PerspectiveCamera(42, window.innerWidth / window.innerHeight, 0.1, 30);
  camera.position.z = camera.aspect < 1 ? 8.5 : 6.3;
  camera.position.y = 0.15;
  scene.add(new THREE.HemisphereLight(0xe8f2ff, 0x282128, 2.5));
  const light = new THREE.DirectionalLight(0xffffff, 4);
  light.position.set(-3, 4, 5);
  scene.add(light);
  const rim = new THREE.DirectionalLight(0xff3038, 3);
  rim.position.set(3, 1, -2);
  scene.add(rim);
  const model = new THREE.Group();
  scene.add(model);
  const materials = [];
  const geometries = [];
  const parts = [];
  const material = (color, metalness = 0.45, roughness = 0.3) => {
    const value = new THREE.MeshStandardMaterial({ color, metalness, roughness, transparent: true });
    materials.push(value);
    return value;
  };
  const silver = material(0xe0e4e8);
  const dark = material(0x20252b, 0.6);
  const red = material(0xcb1828);
  const eye = material(0x91ffdb, 0.1);
  eye.emissive.setHex(0x2dffc5);
  // Beveled polygon plates give the kit a faceted, manufactured silhouette.
  const plate = (points, depth, surface, position, offset, delay = 0) => {
    const shape = new THREE.Shape();
    points.forEach(([x, y], i) => i ? shape.lineTo(x, y) : shape.moveTo(x, y));
    shape.closePath();
    const geometry = new THREE.ExtrudeGeometry(shape, {
      depth, bevelEnabled: true, bevelSegments: 1, steps: 1, bevelSize: 0.018, bevelThickness: 0.018
    });
    geometries.push(geometry);
    const mesh = new THREE.Mesh(geometry, surface);
    const target = new THREE.Vector3(...position);
    model.add(mesh);
    parts.push({ mesh, target, offset: new THREE.Vector3(...offset), delay });
    return mesh;
  };
  plate([[-0.38, -0.43], [0.38, -0.43], [0.49, 0.35], [0.28, 0.64], [-0.28, 0.64], [-0.49, 0.35]], 0.38, dark, [0, 0.15, -0.25], [0, 0, -0.6]);
  plate([[-0.25, -0.22], [0, -0.45], [0.25, -0.22], [0.21, 0.05], [0, 0.16], [-0.21, 0.05]], 0.15, silver, [0, 0.12, 0.3], [0, -0.4, 0.9], 0.08);
  plate([[-0.09, -0.31], [0.09, -0.31], [0.11, -0.15], [0, -0.02], [-0.11, -0.15]], 0.12, red, [0, 0.09, 0.5], [0, -0.4, 1.1], 0.16);
  plate([[-0.15, 0.18], [0.15, 0.18], [0.23, 0.6], [0, 0.81], [-0.23, 0.6]], 0.18, silver, [0, 0.16, 0.14], [0, 0.6, 0.15], 0.05);
  plate([[-0.07, 0.27], [0.07, 0.27], [0.1, 0.5], [0, 0.6], [-0.1, 0.5]], 0.05, red, [0, 0.18, 0.36], [0, 0.75, 0.3], 0.1);
  for (const side of [-1, 1]) {
    const mirror = points => points.map(([x, y]) => [x * side, y]);
    plate(mirror([[0.25, -0.42], [0.53, -0.3], [0.59, 0.24], [0.4, 0.52], [0.23, 0.36], [0.35, 0.1]]), 0.23, silver, [0, 0.13, 0.08], [side * 0.8, 0.2, 0.15], 0.08);
    plate(mirror([[0.13, 0.36], [0.27, 0.35], [0.91, 1.22], [0.41, 0.89]]), 0.08, silver, [0, 0.17, 0.3], [side * 0.45, 0.5, 0], 0.16);
    plate(mirror([[0.2, 0.43], [0.26, 0.44], [0.67, 0.99], [0.4, 0.73]]), 0.025, red, [0, 0.17, 0.4], [side * 0.45, 0.5, 0], 0.16);
    plate(mirror([[0.04, 0.14], [0.28, 0.21], [0.26, 0.11], [0.07, 0.06]]), 0.04, eye, [0, 0.19, 0.32], [side * 0.2, 0, 0.1], 0.04);
    plate(mirror([[0.11, -0.65], [0.55, -0.46], [0.78, -0.69], [0.58, -1.1], [0.14, -0.96]]), 0.3, dark, [0, 0.1, -0.17], [side * 0.5, -0.4, -0.1]);
    plate(mirror([[0.62, -0.46], [1.04, -0.39], [1.23, -0.65], [1.11, -0.96], [0.76, -0.84]]), 0.3, silver, [0, 0.1, -0.05], [side * 0.9, -0.25, 0], 0.12);
    plate(mirror([[0.14, -0.62], [0.53, -0.53], [0.67, -0.66], [0.43, -0.8], [0.13, -0.77]]), 0.08, red, [0, 0.1, 0.18], [side * 0.7, -0.2, 0.5], 0.16);
    for (let slot = 0; slot < 3; slot++) {
      plate(mirror([[0.8, -0.57 - slot * 0.075], [1.06, -0.58 - slot * 0.075], [1.02, -0.615 - slot * 0.075], [0.8, -0.605 - slot * 0.075]]), 0.02, dark, [0, 0.1, 0.27], [side * 0.9, -0.25, 0], 0.12);
    }
  }
  let frame;
  let disposed = false;
  const duration = window.innerWidth < 768 ? 1200 : 1500;
  const started = performance.now();
  const dispose = () => {
    if (disposed) return;
    disposed = true;
    cancelAnimationFrame(frame);
    canvas.remove();
    geometries.forEach(geometry => geometry.dispose());
    materials.forEach(surface => surface.dispose());
    renderer.dispose();
    renderer.forceContextLoss();
    window.removeEventListener('pagehide', dispose);
    window.removeEventListener('resize', dispose);
    window.removeEventListener('pointerdown', dispose, true);
    window.removeEventListener('keydown', dispose, true);
    window.removeEventListener('wheel', dispose);
    document.removeEventListener('visibilitychange', dispose);
    motion.removeEventListener('change', dispose);
  };
  window.addEventListener('pagehide', dispose, { once: true });
  window.addEventListener('resize', dispose, { once: true });
  window.addEventListener('pointerdown', dispose, { once: true, capture: true });
  window.addEventListener('keydown', dispose, { once: true, capture: true });
  window.addEventListener('wheel', dispose, { once: true, passive: true });
  document.addEventListener('visibilitychange', dispose, { once: true });
  motion.addEventListener('change', dispose, { once: true });
  const tick = now => {
    if (disposed) return;
    const progress = Math.min((now - started) / duration, 1);
    for (const part of parts) {
      const t = THREE.MathUtils.clamp((progress - part.delay) / 0.4, 0, 1);
      const remaining = (1 - t) ** 3;
      part.mesh.position.copy(part.target).addScaledVector(part.offset, remaining);
      part.mesh.rotation.y = remaining * part.offset.x * 0.4;
    }
    const appear = Math.min(progress / 0.09, 1);
    const exit = THREE.MathUtils.smoothstep(progress, 0.72, 1);
    model.rotation.y = -0.3 + 0.5 * progress;
    model.rotation.x = 0.07;
    model.position.z = exit * 0.8;
    const opacity = appear * (1 - exit);
    materials.forEach(surface => { surface.opacity = opacity; });
    eye.emissiveIntensity = THREE.MathUtils.smoothstep(progress, 0.4, 0.6) * 3;
    canvas.style.backgroundColor = `rgba(8, 10, 13, ${opacity * 0.82})`;
    try { renderer.render(scene, camera); } catch { dispose(); return; }
    if (progress >= 1) dispose();
    else frame = requestAnimationFrame(tick);
  };
  document.body.append(canvas);
  tick(started);
}
